import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle, openAs } from "../core/oda";
import { applyScales2D } from "../blocks/transform";
import { selectionService } from "../selection";
import { OpenMode } from "../core/constants";
import { recordGraphicsModified } from "../entities";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface AttrScaleSnapshot {
  handle: string;
  height: number;
  alignmentX: number;
  alignmentY: number;
}

export interface SetPhotoMarkerScaleParams {
  blockHandle: string;
  oldBlockScale: number;
  newBlockScale: number;
  oldAttrs: AttrScaleSnapshot[];
  newAttrs: AttrScaleSnapshot[];
}

export class SetPhotoMarkerScaleCommand implements ICommand {
  private oplogEvents: OplogEmit[] = [];

  constructor(
    private readonly viewer: DRDrawingViewer,
    private readonly params: SetPhotoMarkerScaleParams,
  ) {}

  /** Oplog émis sur `blockHandle` (le marqueur) — pas sur chaque attribut. */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      { handle: this.params.blockHandle, changeKind: "scale" },
    ]);
  }

  undo(): void {
    this.apply(this.params.oldBlockScale, this.params.oldAttrs);
    this.viewer.update();
    selectionService.updateGrid();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    this.apply(this.params.newBlockScale, this.params.newAttrs);
    this.viewer.update();
    selectionService.updateGrid();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {}

  private apply(blockScale: number, attrs: AttrScaleSnapshot[]): void {
    const { module, database } = this.viewer;

    const blockId = findEntityByHandle(
      module,
      database,
      this.params.blockHandle,
    );
    if (!blockId || blockId.isNull()) return;

    const blockObj = blockId.safeOpenObject(OpenMode.kForWrite, false);
    const blockRef = module.OdDbBlockReference.cast(blockObj);
    if (blockRef) {
      applyScales2D(module, blockRef, blockScale, blockScale, 1);
      recordGraphicsModified(module, blockId);
      deleteObj(blockRef);
    } else {
      deleteObj(blockObj);
    }
    deleteObj(blockId);

    for (const snap of attrs) {
      const attrId = findEntityByHandle(module, database, snap.handle);
      if (!attrId || attrId.isNull()) continue;
      const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
      if (attr) {
        attr.setHeight(snap.height);
        const pt = new module.OdGePoint3d(snap.alignmentX, snap.alignmentY, 0);
        attr.setAlignmentPoint(pt);
        recordGraphicsModified(module, attrId);
        deleteObj(pt);
        deleteObj(attr);
      }
      deleteObj(attrId);
    }
  }
}
